import React, { useState, useEffect } from 'react';
import { QrCode } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './UpgradeModal.css';

const FEATURE_ROWS = [
  { label: 'Credits / month',    starter: '15 credits',   pro: '300 credits' },
  { label: 'Job Tracker',        starter: '✓',            pro: '✓' },
  { label: 'Calendar',           starter: 'Basic only',   pro: 'Full + Email Reminders' },
  { label: 'Custom Event Types', starter: '—',            pro: '✓' },
  { label: 'AI Job Parsing',     starter: 'URL only',     pro: 'URL + Resume PDF' },
  { label: 'AI Analysis',        starter: 'Limited',      pro: 'Full access' },
  { label: 'Interview Prep',     starter: 'Limited',      pro: 'Full access' },
  { label: 'Job Comparison',     starter: 'Basic only',   pro: 'Basic + AI Analysis' },
  { label: 'Resume Upload',      starter: '—',            pro: '✓' },
  { label: 'Support',            starter: 'Standard',     pro: 'High Priority' },
];

const BILLING_PLANS = [
  {
    id: 'monthly',
    label: 'Monthly',
    totalPHP: 199,
    perMonthPHP: 199,
    months: 1,
    cardFee: 10,
    badge: null,
    savingsPHP: null,
  },
  {
    id: '3month',
    label: '3 Months',
    totalPHP: 549,
    perMonthPHP: 183,
    months: 3,
    cardFee: 15,
    badge: 'Most Popular',
    savingsPHP: 48,
  },
  {
    id: '6month',
    label: '6 Months',
    totalPHP: 999,
    perMonthPHP: 166,
    months: 6,
    cardFee: 30,
    badge: 'Best Value',
    savingsPHP: 195,
  },
];

const PAYMENT_METHODS = [
  { id: 'qrph',  label: 'QR Ph',              available: true },
  { id: 'gcash', label: 'GCash',              available: false },
  { id: 'card',  label: 'Visa / Mastercard',  available: false },
];

const PayMethodIcon = ({ id }) => {
  if (id === 'qrph') return (
    <QrCode size={22} strokeWidth={1.8} color="#005BAC" />
  );
  if (id === 'gcash') return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#007DFD" />
      <text x="12" y="16.5" textAnchor="middle" fill="white" fontWeight="900" fontSize="13" fontFamily="Arial, sans-serif">G</text>
    </svg>
  );
  if (id === 'card') return (
    <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
        <rect width="28" height="18" rx="2.5" fill="#1A1F71" />
        <text x="14" y="13" textAnchor="middle" fill="white" fontWeight="900" fontSize="8.5" fontFamily="Arial, sans-serif" letterSpacing="1.5">VISA</text>
      </svg>
      <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
        <rect width="28" height="18" rx="2.5" fill="#F5F5F5" />
        <circle cx="11" cy="9" r="6" fill="#EB001B" />
        <circle cx="17" cy="9" r="6" fill="#F79E1B" />
        <path d="M14 3.8C15.5 4.9 16.5 6.85 16.5 9C16.5 11.15 15.5 13.1 14 14.2C12.5 13.1 11.5 11.15 11.5 9C11.5 6.85 12.5 4.9 14 3.8Z" fill="#FF5F00" />
      </svg>
    </span>
  );
  return null;
};

// ₱199 ≈ $3.50 → ₱1 ≈ $0.01759
const toUSD = (php) => (php * 0.01759).toFixed(2);

const UpgradeModal = ({ onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('PHP');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  const [billingPlan, setBillingPlan] = useState('3month');
  const [paymentMethod, setPaymentMethod] = useState('qrph');

  const isPro = user?.tier === 'pro';
  const plan = BILLING_PLANS.find(p => p.id === billingPlan);
  const method = PAYMENT_METHODS.find(m => m.id === paymentMethod);
  const cardFee = plan?.cardFee || 0;
  const methodFee = (paymentMethod === 'card' && PAYMENT_METHODS.find(m => m.id === 'card')?.available) ? cardFee : 0;
  const totalPHP = (plan?.totalPHP || 199) + methodFee;
  const totalDisplay = currency === 'PHP' ? `₱${totalPHP}` : `$${toUSD(totalPHP)}`;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/payment/create-checkout', {
        paymentMethod,
        plan: billingPlan,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };


  return (
    <div className="upgrade-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="upgrade-panel">
        <div className="upgrade-panel-scroll">

          {/* Header */}
          <div className="upgrade-header">
            <div className="upgrade-header-text">
              <h2 className="upgrade-title">Upgrade to Pro</h2>
              <p className="upgrade-sub">Unlock the full AI toolkit for your job search</p>
            </div>
            <div className="upgrade-header-controls">
              <div className="upgrade-currency-toggle">
                <button className={`upgrade-currency-btn ${currency === 'PHP' ? 'active' : ''}`} onClick={() => setCurrency('PHP')}>₱ PHP</button>
                <button className={`upgrade-currency-btn ${currency === 'USD' ? 'active' : ''}`} onClick={() => setCurrency('USD')}>$ USD</button>
              </div>
              <button className="upgrade-close" onClick={onClose}>✕</button>
            </div>
          </div>

          <div className="upgrade-body">

            {/* Billing plan selector (non-pro only) */}
            {!isPro && (
              <div className="ubp-section">
                <div className="ubp-section-label">Choose a billing period</div>
                <div className="ubp-plans">
                  {BILLING_PLANS.map(p => (
                    <button
                      key={p.id}
                      className={`ubp-card ${billingPlan === p.id ? 'active' : ''}`}
                      onClick={() => setBillingPlan(p.id)}
                    >
                      {p.badge && <div className={`ubp-badge ${p.id === '6month' ? 'ubp-badge-gold' : ''}`}>{p.badge}</div>}
                      <div className="ubp-plan-name">{p.label}</div>
                      <div className="ubp-price">
                        {currency === 'PHP' ? `₱${p.totalPHP}` : `$${toUSD(p.totalPHP)}`}
                      </div>
                      {p.months > 1 && (
                        <div className="ubp-per-month">
                          {currency === 'PHP' ? `₱${p.perMonthPHP}` : `$${toUSD(p.perMonthPHP)}`}/mo
                        </div>
                      )}
                      {p.savingsPHP && (
                        <div className="ubp-savings">
                          Save {currency === 'PHP' ? `₱${p.savingsPHP}` : `$${toUSD(p.savingsPHP)}`}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Current plan info (pro users) */}
            {isPro && (
              <div className="upgrade-plans">
                <div className="upgrade-plan">
                  <div className="uplan-label">Starter</div>
                  <div className="uplan-price">₱0 <span>/ forever</span></div>
                  <div className="uplan-credits">15 credits / month</div>
                </div>
                <div className="upgrade-plan upgrade-plan-pro upgrade-plan-current">
                  <div className="uplan-pro-badge">Current Plan</div>
                  <div className="uplan-label uplan-label-pro">Pro</div>
                  <div className="uplan-price uplan-price-pro">₱{BILLING_PLANS.find(p => p.id === (user?.subscriptionPlan || 'monthly'))?.totalPHP ?? 199} <span>/{user?.subscriptionPlan === '3month' ? '3 months' : user?.subscriptionPlan === '6month' ? '6 months' : 'month'}</span></div>
                  <div className="uplan-credits uplan-credits-pro">300 credits / month</div>
                  {user?.subscriptionEndsAt && (
                    <div className="uplan-renewal">Renews {new Date(user.subscriptionEndsAt).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  )}
                  <div className="uplan-badge">Active</div>
                </div>
              </div>
            )}

            {/* Feature comparison */}
            <div className="upgrade-table-wrap">
              <table className="upgrade-table">
                <thead>
                  <tr>
                    <th className="utbl-th-feat">Feature</th>
                    <th className="utbl-th-tier">Starter</th>
                    <th className="utbl-th-tier utbl-th-pro">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_ROWS.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'utbl-row-alt' : ''}>
                      <td className="utbl-td-feat">{row.label}</td>
                      <td className="utbl-td-val">{row.starter}</td>
                      <td className="utbl-td-val utbl-td-pro">{row.pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment method selector (non-pro only) */}
            {!isPro && (
              <div className="upgrade-pm-selector">
                <div className="upgrade-pm-label">Pay with</div>
                <div className="upgrade-pm-options">
                  {PAYMENT_METHODS.map(m => (
                    <button
                      key={m.id}
                      className={`upgrade-pm-option ${paymentMethod === m.id ? 'active' : ''} ${!m.available ? 'upgrade-pm-disabled' : ''}`}
                      onClick={() => m.available && setPaymentMethod(m.id)}
                      disabled={!m.available}
                    >
                      <span className="upgrade-pm-option-icon"><PayMethodIcon id={m.id} /></span>
                      <span className="upgrade-pm-option-label">{m.label}</span>
                      {m.available && m.id === 'card' && <span className="upgrade-pm-option-fee">+₱{cardFee}</span>}
                      {!m.available && <span className="upgrade-pm-soon">Soon</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="upgrade-actions">
              {!isPro ? (
                <>
                  <button className="upgrade-btn-pro" onClick={handleUpgrade} disabled={loading}>
                    {loading
                      ? 'Redirecting to checkout...'
                      : `Pay ${totalDisplay} via ${method?.label}`
                    }
                  </button>
                  <div className="upgrade-secure-note">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Secure checkout via PayMongo
                  </div>
                </>
              ) : null}
              <button className="upgrade-btn-cancel" onClick={onClose}>Maybe later</button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
